// sign-console-prefill-2026-07 — 관리 콘솔(상시 온라인) 미리 채우기 패널.
//
// 문서 상세에서 PDF 위에 입력란을 겹쳐(오버레이) 미리 채운 뒤 서버에 저장한다
// (PUT /signature-documents/:id/prefill). 저장값은 원격 서명 링크(getSigningPage)에
// 병합돼 서명자에게 프리필된다. operator-pwa PrefillView 의 오버레이 패턴을 이식하되
// 저장 sink 를 Dexie → 서버로 바꿨다.
//
// 필드 3분류:
//   · 편집 가능 = prefillable(text/date/checkbox/dropdown) + literal 바인딩(수동 입력).
//   · 자동 채움 = 바인딩 필드(로봇 시리얼·모델명·상호명·사업자번호·대표자명·주소 등).
//     시스템이 아는 값이라 서버가 서명 링크·최종 PDF 에 자동 채움 → 읽기 전용 표시.
//   · 서명/도장/이미지 = 고객·현장 수집 → 읽기 전용 표시.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { signatureFieldFontPx, type DataBinding, type SignatureField } from '@bhops/shared';
import { signatureDocumentsApi } from '@/api/signatureDocuments';
import { extractApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { useToast } from '@/components/Toast';
import { PdfCanvasViewer } from './PdfCanvasViewer';
import { exportPrefillImages, resolvePrefillDisplayValue } from './exportPrefillImage';

const PAGE_W = 794;
const PAGE_H = 1122;

const isPrefillable = (f: SignatureField): boolean =>
  f.type === 'text' || f.type === 'date' || f.type === 'checkbox' || f.type === 'dropdown';

// 운영자가 손으로 채우는 필드 = prefillable + literal(자동 바인딩 아님).
const isEditable = (f: SignatureField): boolean => isPrefillable(f) && f.binding.kind === 'literal';

// 값이 실제로 채워졌는지 — 미체크 체크박스('false')는 '빈 값'으로 취급(채움 수·
// 저장에서 제외). 안 그러면 켰다 끈 체크박스가 채움 수를 부풀리고 'false'가 저장됨.
const hasValue = (f: SignatureField, v: string): boolean =>
  f.type === 'checkbox' ? v === 'true' : (v ?? '').trim().length > 0;

function bindingLabel(b: DataBinding): string {
  if (b.kind === 'robot') return b.path === 'modelName' ? '로봇 모델명' : '로봇 시리얼';
  if (b.kind === 'customer') {
    return b.path === 'businessNumber'
      ? '사업자번호'
      : b.path === 'ceoName'
        ? '대표자명'
        : b.path === 'address'
          ? '주소'
          : '상호명';
  }
  if (b.kind === 'signer') return b.path === 'phone' ? '서명자 전화' : '서명자 이름';
  return '자동';
}

// 저장 대상. savePrefill 은 전체 교체(full-replace)이므로, 콘솔이 관리하지 않는
// 키(예: 태블릿이 저장한 값)는 base 로 보존하고, 편집 가능 필드만 현재 값으로
// 덮어쓰거나(값 있음) 삭제한다(운영자가 비움). 이로써 콘솔 저장이 다른 출처의
// 프리필을 통째로 지우지 않는다.
function collectPrefill(
  fields: SignatureField[],
  values: Record<string, string>,
  base: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = { ...base };
  for (const f of fields) {
    if (!isEditable(f)) continue;
    if (hasValue(f, values[f.id] ?? '')) out[f.id] = values[f.id];
    else delete out[f.id];
  }
  return out;
}

interface Props {
  documentId: string;
  fields: SignatureField[];
  pageCount: number;
  /** presigned 베이스 PDF URL (상세 DTO). 패널 열림 시 신선한 URL 을 재조회. */
  fallbackPdfUrl: string;
  initialValues: Record<string, string>;
  onClose: () => void;
  /** 이미지로 저장할 때 쓰는 파일명 앞부분. 없으면 기본값. */
  documentName?: string;
}

export function SignDocumentPrefillPanel({
  documentId,
  fields,
  pageCount,
  fallbackPdfUrl,
  initialValues,
  onClose,
  documentName,
}: Props) {
  const qc = useQueryClient();
  const toast = useToast();

  // presigned URL 60초 TTL 대비 — 패널 열릴 때 신선한 URL 을 받는다. 뷰어에는
  // **신선한 URL 만** 넘긴다(로딩 중엔 플레이스홀더). 상세 DTO 의 fallbackPdfUrl 은
  // 패널을 늦게 열면 이미 만료됐을 수 있어(60초) 조회 실패 시에만 쓴다 — 처음부터
  // 넘기면 만료 URL 로 '로드 실패'가 깜빡였다가 재요청되는 이중 다운로드가 생긴다.
  const pdfQ = useQuery({
    queryKey: ['sign-doc-prefill-pdf', documentId],
    queryFn: () => signatureDocumentsApi.getPdfUrl(documentId),
    staleTime: 50_000,
  });
  const pdfUrl = pdfQ.data?.url ?? (pdfQ.isError ? fallbackPdfUrl : null);

  const [values, setValues] = useState<Record<string, string>>(() => ({ ...initialValues }));

  // 화면 폭에 맞춘 스케일 + 수동 줌 + 발송 전 미리보기(읽기 전용 렌더).
  const viewportRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [preview, setPreview] = useState(false);
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(el.clientWidth, PAGE_W) / PAGE_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const editable = useMemo(() => fields.filter(isEditable), [fields]);
  const filledCount = editable.filter((f) => hasValue(f, values[f.id] ?? '')).length;
  const autoCount = fields.filter((f) => f.binding.kind !== 'literal').length;

  const saveMutation = useMutation({
    mutationFn: () =>
      signatureDocumentsApi.savePrefill(documentId, collectPrefill(fields, values, initialValues)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-document', documentId] });
      toast.success('미리 채우기가 저장되었습니다.');
      onClose();
    },
    onError: (err) => toast.error(extractApiError(err), '미리 채우기 저장 실패'),
  });

  const totalH = pageCount * PAGE_H;

  // ── 미리보기를 PNG 로 저장 ──────────────────────────────────────────────
  // 화면 캡처가 아니라 PDF 를 고배율(≈288dpi)로 다시 렌더하고 값을 그린다 —
  // 화면 줌과 무관하게 항상 같은 화질이고 에디터 크롬이 섞이지 않는다.
  const [exporting, setExporting] = useState(false);
  // 2026-10-20 오너 — '페리페럴 리스트 받기': 2페이지(주변기기 목록)만 PNG 한 장으로.
  const [exportingPeripheral, setExportingPeripheral] = useState(false);
  const handleExportPeripheral = async (): Promise<void> => {
    if (!pdfUrl || exportingPeripheral) return;
    setExportingPeripheral(true);
    try {
      await exportPrefillImages({
        pdfUrl,
        fields,
        values,
        pageCount,
        pages: [2],
        filenameBase: `${documentName || '서명문서'}_페리페럴리스트`,
      });
      toast.success('2페이지 페리페럴 리스트를 이미지로 저장했습니다.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err), '페리페럴 리스트 저장 실패');
    } finally {
      setExportingPeripheral(false);
    }
  };
  const handleExportImage = async (): Promise<void> => {
    if (!pdfUrl || exporting) return;
    setExporting(true);
    try {
      const saved = await exportPrefillImages({
        pdfUrl,
        fields,
        values,
        pageCount,
        filenameBase: `${documentName || '서명문서'}_미리채우기`,
      });
      toast.success(
        saved > 1
          ? `페이지마다 한 장씩, 이미지 ${saved}장을 저장했습니다.`
          : '이미지를 저장했습니다.',
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err), '이미지 저장 실패');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          입력란 {filledCount}/{editable.length} 채움
          {autoCount > 0 && ` · 자동 채움 ${autoCount}개`}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <button
            type="button"
            style={zoomBtn}
            onClick={() => setZoom((z) => Math.max(1, z - 0.5))}
          >
            −
          </button>
          <button type="button" style={zoomBtn} onClick={() => setZoom(1)}>
            맞춤
          </button>
          <button
            type="button"
            style={zoomBtn}
            onClick={() => setZoom((z) => Math.min(3, z + 0.5))}
          >
            +
          </button>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', minWidth: 40 }}>
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            style={{
              ...zoomBtn,
              marginLeft: 6,
              ...(preview ? { background: 'var(--bg-sunken)', color: 'var(--fg-brand)' } : null),
            }}
            onClick={() => setPreview((p) => !p)}
            aria-pressed={preview}
            data-testid="sign-doc-prefill-preview"
            title="직접 입력한 값을 미리 봅니다. 자동 채움 항목과 서명은 발송할 때 채워집니다."
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Icon name={preview ? 'pen' : 'eye'} size={12} aria-hidden />
              {preview ? '편집' : '미리보기'}
            </span>
          </button>
          {/* 페리페럴 리스트 받기 — 편집·미리보기 어느 쪽에서도. 2페이지가 있는 문서에만. */}
          {pageCount >= 2 && (
            <button
              type="button"
              style={{
                ...zoomBtn,
                marginLeft: 6,
                ...(exportingPeripheral ? { opacity: 0.6, cursor: 'default' } : null),
              }}
              onClick={() => void handleExportPeripheral()}
              disabled={exportingPeripheral || !pdfUrl}
              data-testid="sign-doc-prefill-export-peripheral"
              title="2페이지 페리페럴 리스트만 지금 채운 값으로 고화질 PNG 한 장에 저장합니다"
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Icon name="download" size={12} aria-hidden />
                {exportingPeripheral ? '저장 중…' : '페리페럴 리스트 받기'}
              </span>
            </button>
          )}
          {/* 미리보기 상태에서만 노출 — 지금 보고 있는 화면을 파일로 남기는 동작이라
              편집 중에 보이면 '무엇이 저장되는지'가 모호해진다. */}
          {preview && (
            <button
              type="button"
              style={{ ...zoomBtn, ...(exporting ? { opacity: 0.6, cursor: 'default' } : null) }}
              onClick={() => void handleExportImage()}
              disabled={exporting || !pdfUrl}
              data-testid="sign-doc-prefill-export-image"
              title="지금 채운 값이 들어간 문서를 고화질 PNG로 저장합니다. 자동 채움 항목과 서명은 발송할 때 채워지므로 비어 있습니다."
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Icon name="download" size={12} aria-hidden />
                {exporting ? '저장 중…' : '이미지로 저장'}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* 범례 */}
      <div
        style={{
          display: 'flex',
          gap: 14,
          fontSize: 11,
          color: 'var(--text-secondary)',
          flexWrap: 'wrap',
        }}
      >
        <span style={legendItem}>
          <span
            style={{
              ...swatch,
              borderColor: 'var(--fg-brand)',
              background: 'color-mix(in srgb, var(--fg-brand) 8%, transparent)',
            }}
          />
          직접 입력
        </span>
        <span style={legendItem}>
          <span
            style={{
              ...swatch,
              borderStyle: 'solid',
              borderColor: 'var(--border-default)',
              background: 'var(--bg-sunken)',
            }}
          />
          시스템 자동 채움
        </span>
        <span style={legendItem}>
          <span style={{ ...swatch, borderColor: 'var(--border-default)' }} />
          고객 서명·도장
        </span>
      </div>

      {pdfUrl ? (
        <div
          ref={viewportRef}
          style={{
            overflow: 'auto',
            maxHeight: 'calc(100vh - 320px)',
            background: 'var(--bg-sunken)',
            borderRadius: 'var(--r-card)',
            border: '1px solid var(--border-default)',
          }}
        >
          <div
            style={{
              width: PAGE_W * scale * zoom,
              height: totalH * scale * zoom,
              margin: '0 auto',
            }}
          >
            <div
              style={{
                position: 'relative',
                width: PAGE_W,
                height: totalH,
                transform: `scale(${scale * zoom})`,
                transformOrigin: 'top left',
              }}
            >
              <PdfCanvasViewer url={pdfUrl} pageWidth={PAGE_W} pageHeight={PAGE_H} />
              {fields.map((field) => (
                <PrefillFieldOverlay
                  key={field.id}
                  field={field}
                  value={values[field.id] ?? ''}
                  readOnly={preview}
                  onChange={(v) => setValues((prev) => ({ ...prev, [field.id]: v }))}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: 24, color: 'var(--text-secondary)', fontSize: 13 }}>
          PDF 를 불러오는 중…
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" style={btnGhost} onClick={onClose} disabled={saveMutation.isPending}>
          닫기
        </button>
        <button
          type="button"
          style={{ ...btnPrimary, opacity: saveMutation.isPending ? 0.5 : 1 }}
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          data-testid="sign-doc-prefill-save"
        >
          {saveMutation.isPending ? '저장 중…' : '미리 채우기 저장'}
        </button>
      </div>
    </div>
  );
}

function PrefillFieldOverlay({
  field,
  value,
  onChange,
  readOnly,
}: {
  field: SignatureField;
  value: string;
  onChange: (v: string) => void;
  readOnly: boolean;
}) {
  const box: CSSProperties = {
    position: 'absolute',
    left: field.x,
    top: field.y,
    width: field.w,
    height: field.h,
    boxSizing: 'border-box',
  };

  // 서명/도장/이미지 = 고객·현장 수집 → 읽기 전용.
  if (field.type === 'signature' || field.type === 'stamp' || field.type === 'image') {
    return (
      <div
        style={{
          ...box,
          border: '1px dashed var(--border-default)',
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          background: 'color-mix(in srgb, var(--fg-muted) 6%, transparent)',
        }}
      >
        <span style={{ fontSize: 10, color: 'var(--fg-muted)' }}>
          {field.type === 'signature' ? '서명' : field.type === 'stamp' ? '도장' : ''}
        </span>
      </div>
    );
  }

  // 바인딩 필드(자동 채움) → 읽기 전용 회색 칩. 시스템이 아는 값이라 손입력 불가.
  if (field.binding.kind !== 'literal') {
    return (
      <div
        style={{
          ...box,
          border: '1px solid var(--border-default)',
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent:
            field.align === 'center'
              ? 'center'
              : field.align === 'right'
                ? 'flex-end'
                : 'flex-start',
          padding: '1px 4px',
          pointerEvents: 'none',
          background: 'var(--bg-sunken)',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
        title="시스템이 자동으로 채우는 값"
      >
        <span
          style={{ fontSize: Math.min(11, Math.max(field.h, 12) * 0.45), color: 'var(--fg-muted)' }}
        >
          자동: {bindingLabel(field.binding)}
        </span>
      </div>
    );
  }

  // 2026-10-20 — 표 칸이 만든 입력 칸은 칸 글자 크기(signatureFieldFontPx), 아니면 예전 규칙.
  const fontSize = signatureFieldFontPx(field, Math.min(12, Math.max(field.h, 12) * 0.5));

  // 발송 전 미리보기 — 입력 크롬 없이 채운 값만 필드 위치에 표시(정렬 반영).
  // 서버 PDF 스탬프와 동일한 좌표/정렬을 화면에서 근사(직접 입력 필드만; 자동 채움·
  // 서명 필드는 위 분기에서 이미 처리). 서버가 빈 값에 넣는 기본값도 반영:
  // 빈 날짜 → 오늘(formatDate=YYYY-MM-DD), 빈 text/dropdown → defaultValue.
  if (readOnly) {
    // 체크박스는 서버 drawCheckbox 와 같은 모양(변 = min(w,h)*0.7 사각 테두리 +
    // 체크 표시)으로 근사한다 — 글리프로 그리면 폰트마다 크기가 달라진다.
    const boxSide = Math.min(field.w, field.h) * 0.7;
    const display =
      field.type === 'checkbox' ? (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: boxSide,
            height: boxSide,
            border: '1px solid var(--text-primary)',
            borderRadius: 2,
          }}
        >
          {value === 'true' && <Icon name="check" size={boxSide * 0.9} aria-hidden />}
        </span>
      ) : (
        // 이미지 내보내기와 규칙이 갈리지 않도록 공용 헬퍼를 쓴다.
        //
        // 합류(26-08-05) — 종전 이 자리에 있던 세 갈래(값 / 빈 date 는 오늘 / defaultValue)를
        // 헬퍼 한 벌로 모은다. 그 세 갈래 중 '빈 date 는 오늘'의 **KST 처리**(추가수정 2차
        // 26-07-27 §2 — UTC 면 한국 오전 9시 전에 어제 날짜가 떴다)는 헬퍼 안으로 옮겼다.
        // 화면과 PNG 내보내기가 같은 함수를 쓰므로 그 수정이 양쪽에 함께 적용된다.
        resolvePrefillDisplayValue(field, value)
      );
    return (
      <div
        style={{
          ...box,
          display: 'flex',
          alignItems: 'center',
          justifyContent:
            field.type === 'checkbox'
              ? 'center'
              : field.align === 'center'
                ? 'center'
                : field.align === 'right'
                  ? 'flex-end'
                  : 'flex-start',
          padding: '1px 4px',
          fontSize,
          color: 'var(--text-primary)',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}
      >
        {display}
      </div>
    );
  }

  // 직접 입력 필드.
  const filled = (value ?? '').trim().length > 0;
  const border = filled
    ? '1px solid color-mix(in srgb, var(--fg-brand) 35%, transparent)'
    : '1px dashed color-mix(in srgb, var(--fg-brand) 55%, transparent)';
  const bg = filled ? 'transparent' : 'color-mix(in srgb, var(--fg-brand) 6%, transparent)';

  if (field.type === 'checkbox') {
    return (
      <div style={{ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <input
          type="checkbox"
          checked={value === 'true'}
          onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          style={{ width: 18, height: 18 }}
          aria-label="미리 채우기 체크박스"
        />
      </div>
    );
  }

  if (field.type === 'dropdown') {
    return (
      <div style={box}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            // block — 인라인이면 글줄 기준선에 맞춰 칸 아래로 ~5px 내려앉는다(2026-10-20).
            display: 'block',
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            border,
            borderRadius: 3,
            background: bg,
            textAlign: field.align ?? 'left',
            fontSize,
          }}
        >
          <option value="">{field.placeholder ?? '선택'}</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div style={box}>
      <input
        type={field.type === 'date' ? 'date' : 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder ?? ''}
        style={{
          // block — 인라인 input 은 부모 글줄의 기준선에 앉아 작은 칸(13px)에서 ~5px 아래로
          // 밀려 보였다(2026-10-20 오너: "아직도 칸 안맞는데?"). 좌표는 맞았고 그리는 자리만 어긋났다.
          display: 'block',
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          border,
          borderRadius: 3,
          background: bg,
          padding: '1px 4px',
          textAlign: field.align ?? 'left',
          fontSize,
        }}
      />
    </div>
  );
}

const btnPrimary: CSSProperties = {
  padding: '8px 16px',
  background: 'var(--fg-brand)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--r-btn)',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
};
const btnGhost: CSSProperties = {
  padding: '8px 14px',
  background: 'var(--bg-surface)',
  color: 'var(--text-secondary)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  fontSize: 13,
  cursor: 'pointer',
};
const zoomBtn: CSSProperties = {
  padding: '3px 9px',
  background: 'var(--bg-surface)',
  color: 'var(--text-secondary)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  fontSize: 12,
  cursor: 'pointer',
};
const legendItem: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5 };
const swatch: CSSProperties = {
  width: 14,
  height: 12,
  borderRadius: 2,
  border: '1px dashed',
  display: 'inline-block',
};
